// src/views/hrms/attendence/RegularizationList.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, CalendarRange, Plus, X } from "lucide-react";

import {
  getRegularizations,
  getRegularization,
  approveRegularization,
  rejectRegularization,
  createRegularization,
  getEmployees,
} from "services/hrms.api";
import Loading from "components/common/Loading";
import RegularizationTable, {
  requestedText,
} from "views/hrms/attendence/RegularizationTable";

/* ---------- helpers ---------- */
const TYPE_LABEL = {
  punch: "Add Punch",
  working_hours: "Working Hours",
  full_day: "Full Day",
  half_day: "Half Day",
  week_off: "Week Off",
};

const IST = "Asia/Kolkata";
const PAGE_SIZE = 10;

const fmtT = (d) =>
  d
    ? new Date(d).toLocaleTimeString("en-IN", {
        timeZone: IST,
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

const fmtDT = (d) =>
  d
    ? new Date(d).toLocaleString("en-GB", {
        timeZone: IST,
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

const fmtDay = (ymd) => {
  const x = new Date(`${ymd}T00:00:00+05:30`);
  return isNaN(x)
    ? ymd || "-"
    : x.toLocaleDateString("en-GB", {
        timeZone: IST,
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

// "2026-10-05" + "10:30" (entered as IST) -> ISO string
const istISO = (day, hhmm) =>
  new Date(`${day}T${hhmm}:00+05:30`).toISOString();

const todayStr = () => new Date().toLocaleDateString("en-CA", { timeZone: IST });

const STATUS_TONE = {
  Pending: "bg-amber-100 text-amber-700",
  Approved: "bg-emerald-100 text-emerald-700",
  Rejected: "bg-rose-100 text-rose-700",
};

const inputCls =
  "dark:bg-transparent rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700";
const primaryBtn =
  "rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-400 dark:hover:bg-brand-300";
const outlineBtn =
  "rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-white dark:hover:bg-white/10";

/* ---------- page ---------- */
export default function RegularizationList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // filters
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("Pending");
  const [type, setType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);

  const [review, setReview] = useState(null); // { id, focus }
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState("");

  // debounced search
  const [qDebounced, setQDebounced] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const normalize = useCallback(
    (arr) =>
      (arr || []).map((r) => ({
        ...r,
        id: r._id || r.id,
        employeeName: r.employee?.name || "-",
        employeeId: r.employee?.code || "",
        typeLabel: TYPE_LABEL[r.type] || r.type,
      })),
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await getRegularizations({ limit: 500 });
      setRows(normalize(data?.items));
    } catch (e) {
      console.error("[RegularizationList] load error:", e);
      setLoadError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [normalize]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(""), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  /* ----- direct approve from the table (reject goes through the modal) ----- */
  const approve = async (id) => {
    if (!window.confirm("Approve this request and apply it to attendance?"))
      return;
    try {
      const { data } = await approveRegularization(id, "");
      setToast(data?.message || "Approved");
      load();
    } catch (e) {
      setToast(e.message);
    }
  };

  /* ----- filters / search ----- */
  const matchesSearch = (row, k) => {
    if (!k) return true;
    const v = k.toLowerCase();
    return (
      (row.employeeName || "").toLowerCase().includes(v) ||
      (row.employeeId || "").toLowerCase().includes(v) ||
      (row.typeLabel || "").toLowerCase().includes(v) ||
      (row.status || "").toLowerCase().includes(v) ||
      (row.reason || "").toLowerCase().includes(v) ||
      (row.workDay || "").includes(v)
    );
  };

  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (status === "All" || r.status === status) &&
          (!type || r.type === type) &&
          (!from || r.workDay >= from) &&
          (!to || r.workDay <= to) &&
          matchesSearch(r, qDebounced)
      ),
    [rows, status, type, from, to, qDebounced]
  );

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE) || 1);

  useEffect(() => {
    setPage(1);
  }, [qDebounced, status, type, from, to]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
    if (page < 1) setPage(1);
  }, [page, pageCount]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex =
    filtered.length === 0 ? 0 : Math.min(filtered.length, page * PAGE_SIZE);

  const pending = rows.filter((r) => r.status === "Pending").length;
  const approved = rows.filter((r) => r.status === "Approved").length;
  const rejected = rows.filter((r) => r.status === "Rejected").length;
  const thisMonth = rows.filter((r) => {
    const d = new Date(`${r.workDay}T00:00:00+05:30`);
    const now = new Date();
    return (
      d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
    );
  }).length;

  if (loading && rows.length === 0) {
    return (
      <div className="p-4 md:p-6">
        <Loading variant="dots" fullscreen />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h2 className="text-2xl font-semibold dark:text-white">
          Manage Regularization
        </h2>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className={`${primaryBtn} inline-flex items-center gap-2`}
        >
          <Plus className="h-4 w-4" />
          Add Regularization
        </button>
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 p-3 dark:border-white/10">
        <div className="flex items-center gap-2 dark:border-white/10 dark:text-white">
          <CalendarRange className="h-4 w-4 text-gray-500 dark:text-white" />
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className={inputCls}
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className={inputCls}
          />
        </div>

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className={inputCls}
        >
          {["Pending", "Approved", "Rejected", "All"].map((s) => (
            <option key={s} value={s}>
              {s === "All" ? "All statuses" : s}
            </option>
          ))}
        </select>

        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className={inputCls}
        >
          <option value="">All types</option>
          {Object.entries(TYPE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>

        <div className="relative ml-auto w-full sm:w-80">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search employee / id / type / reason…"
            className="dark:bg-transparent w-full rounded-lg border border-gray-300 px-3 py-2 pl-9 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700 focus:dark:ring-white"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat label="Pending" value={pending} tone="bg-amber-50 text-amber-700" />
        <Stat label="Approved" value={approved} tone="bg-blue-50 text-emerald-700" />
        <Stat label="Rejected" value={rejected} tone="bg-amber-50 text-rose-700" />
        <Stat label="This Month" value={thisMonth} />
      </div>

      {loadError && (
        <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {loadError}
        </div>
      )}

      {loading ? (
        <TableSkeleton />
      ) : filtered.length ? (
        <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-white/10">
          <RegularizationTable
            rows={pagedRows}
            onApprove={approve}
            onReject={(id) => setReview({ id, focus: true })}
            onView={(id) => setReview({ id, focus: false })}
          />

          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 text-sm text-gray-600 dark:border-white/10 dark:text-white/70 md:flex-row md:items-center md:justify-between">
            <div>
              Showing <span className="font-medium">{startIndex}</span> to{" "}
              <span className="font-medium">{endIndex}</span> of{" "}
              <span className="font-medium">{filtered.length}</span> requests
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className={outlineBtn}
              >
                Previous
              </button>
              <span className="text-xs">
                Page <span className="font-semibold">{page}</span> of{" "}
                <span className="font-semibold">{pageCount}</span>
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                disabled={page >= pageCount}
                className={outlineBtn}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center px-8 py-20 text-center">
          <Search
            className="h-12 w-12 text-gray-300 dark:text-white/20"
            strokeWidth={1.2}
          />
          <h3 className="mt-4 text-lg font-semibold text-gray-800 dark:text-white">
            No requests found
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-white/70">
            Adjust filters or try a different status.
          </p>
        </div>
      )}

      {review && (
        <ReviewModal
          id={review.id}
          focusRemarks={review.focus}
          onClose={() => setReview(null)}
          onDone={(msg) => {
            setReview(null);
            setToast(msg);
            load();
          }}
        />
      )}

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={(msg) => {
            setShowCreate(false);
            setToast(msg);
            load();
          }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 rounded-xl bg-navy-900 px-5 py-3 text-sm text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

/* ---------- modal shell ---------- */
function Modal({ title, onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:text-white dark:hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const ErrorBox = ({ children }) =>
  children ? (
    <div className="mt-3 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
      {children}
    </div>
  ) : null;

/* ---------- review modal ---------- */
function ReviewModal({ id, focusRemarks, onClose, onDone }) {
  const [info, setInfo] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getRegularization(id)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(e.message));
  }, [id]);

  const act = async (action) => {
    if (action === "reject" && remarks.trim().length < 3) {
      setError("Please enter remarks to reject");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const fn =
        action === "approve" ? approveRegularization : rejectRegularization;
      const { data } = await fn(id, remarks.trim());
      onDone(data?.message || "Done");
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const r = info?.request;
  const att = info?.attendance;
  const isPending = r?.status === "Pending";

  return (
    <Modal title="Regularization request" onClose={onClose}>
      {!info && !error && (
        <p className="text-sm text-gray-500 dark:text-white/70">Loading…</p>
      )}

      {r && (
        <>
          <dl className="mb-4 grid grid-cols-[90px_1fr] gap-x-3 gap-y-2 text-sm dark:text-white">
            <dt className="text-gray-500 dark:text-white/70">Employee</dt>
            <dd>
              {r.employee?.name} ({r.employee?.code})
            </dd>
            <dt className="text-gray-500 dark:text-white/70">Date</dt>
            <dd>{fmtDay(r.workDay)}</dd>
            <dt className="text-gray-500 dark:text-white/70">Request</dt>
            <dd>
              {TYPE_LABEL[r.type]} · {requestedText(r)}
            </dd>
            <dt className="text-gray-500 dark:text-white/70">Reason</dt>
            <dd>{r.reason}</dd>
            <dt className="text-gray-500 dark:text-white/70">Applied</dt>
            <dd>{fmtDT(r.appliedAt)}</dd>
            {!isPending && (
              <>
                <dt className="text-gray-500 dark:text-white/70">Decision</dt>
                <dd>
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-semibold ${STATUS_TONE[r.status]}`}
                  >
                    {r.status}
                  </span>{" "}
                  {fmtDT(r.reviewedAt)}
                  {r.remarks ? ` — ${r.remarks}` : ""}
                </dd>
              </>
            )}
          </dl>

          <h4 className="mb-1 text-sm font-semibold dark:text-white">
            Current attendance for this day
          </h4>
          {att?.shiftSnapshot?.startTime && (
            <p className="text-xs text-gray-500 dark:text-white/70">
              Shift: {att.shiftSnapshot.shiftName} {att.shiftSnapshot.startTime}–
              {att.shiftSnapshot.endTime}
            </p>
          )}
          {att?.status && (
            <p className="text-xs text-gray-500 dark:text-white/70">
              Day status: {att.status}
            </p>
          )}
          {att?.sessions?.length ? (
            <table className="my-2 w-full text-left text-xs dark:text-white">
              <thead>
                <tr className="text-gray-500 dark:text-white/70">
                  <th className="py-1">In</th>
                  <th className="py-1">Out</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {att.sessions.map((s, i) => (
                  <tr key={i} className="border-t border-gray-100 dark:border-white/10">
                    <td className="py-1">{fmtT(s.in)}</td>
                    <td className="py-1">{s.out ? fmtT(s.out) : "open"}</td>
                    <td className="py-1 text-gray-500 dark:text-white/70">
                      {s.regularized ? "regularized" : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="my-2 text-xs text-gray-500 dark:text-white/70">
              No punches recorded for this day.
            </p>
          )}

          {isPending && (
            <label className="mt-3 block text-sm font-medium dark:text-white">
              Remarks{" "}
              <span className="font-normal text-gray-500 dark:text-white/70">
                (required to reject)
              </span>
              <textarea
                rows={3}
                autoFocus={focusRemarks}
                className={`${inputCls} mt-1 w-full`}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </label>
          )}
        </>
      )}

      <ErrorBox>{error}</ErrorBox>

      {isPending && (
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={() => act("reject")}
            className="rounded-lg border border-rose-300 px-4 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-400/40 dark:text-rose-300 dark:hover:bg-white/10"
          >
            Reject
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => act("approve")}
            className={primaryBtn}
          >
            {busy ? "Working…" : "Approve"}
          </button>
        </div>
      )}
    </Modal>
  );
}

/* ---------- admin: add regularization for an employee ---------- */
function CreateModal({ onClose, onDone }) {
  const [employees, setEmployees] = useState([]);
  const [employee, setEmployee] = useState("");
  const [workDay, setWorkDay] = useState(todayStr());
  const [type, setType] = useState("punch");
  const [t1, setT1] = useState("");
  const [t2, setT2] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getEmployees()
      .then(({ data }) =>
        setEmployees(Array.isArray(data) ? data : data?.items || [])
      )
      .catch((e) => setError(e.message));
  }, []);

  const needsTime = type === "punch" || type === "working_hours";

  const submit = async () => {
    setError("");
    if (!employee) return setError("Select an employee");
    if (reason.trim().length < 3) return setError("Reason is required");
    if (needsTime && !t1) return setError("Time is required");
    if (type === "working_hours" && !t2) return setError("End time is required");

    const body = { employee, workDay, type, reason: reason.trim() };
    if (needsTime) body.time = istISO(workDay, t1);
    if (type === "working_hours") body.endTime = istISO(workDay, t2);

    setBusy(true);
    try {
      const { data } = await createRegularization(body);
      onDone(data?.message || "Applied");
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const label = "text-sm font-medium dark:text-white";

  return (
    <Modal title="Add regularization for employee" onClose={onClose}>
      <div className="grid gap-3">
        <label className={label}>
          Employee
          <select
            className={`${inputCls} mt-1 w-full`}
            value={employee}
            onChange={(e) => setEmployee(e.target.value)}
          >
            <option value="">Select…</option>
            {employees.map((e) => (
              <option key={e._id || e.id} value={e._id || e.id}>
                {e.name}
                {e.employeeId || e.code ? ` (${e.employeeId || e.code})` : ""}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className={label}>
            Date
            <input
              type="date"
              max={todayStr()}
              className={`${inputCls} mt-1 w-full`}
              value={workDay}
              onChange={(e) => setWorkDay(e.target.value)}
            />
          </label>
          <label className={label}>
            Type
            <select
              className={`${inputCls} mt-1 w-full`}
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {Object.entries(TYPE_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
        </div>

        {needsTime && (
          <div className="grid grid-cols-2 gap-3">
            <label className={label}>
              {type === "punch" ? "Punch time (IST)" : "Start (IST)"}
              <input
                type="time"
                className={`${inputCls} mt-1 w-full`}
                value={t1}
                onChange={(e) => setT1(e.target.value)}
              />
            </label>
            {type === "working_hours" && (
              <label className={label}>
                End (IST)
                <input
                  type="time"
                  className={`${inputCls} mt-1 w-full`}
                  value={t2}
                  onChange={(e) => setT2(e.target.value)}
                />
              </label>
            )}
          </div>
        )}

        <label className={label}>
          Reason
          <textarea
            rows={3}
            className={`${inputCls} mt-1 w-full`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
      </div>

      <ErrorBox>{error}</ErrorBox>

      <div className="mt-4 flex justify-end gap-3">
        <button type="button" onClick={onClose} className={outlineBtn}>
          Cancel
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={submit}
          className={primaryBtn}
        >
          {busy ? "Applying…" : "Apply"}
        </button>
      </div>
    </Modal>
  );
}

/* ---------- tiny UI atoms ---------- */
function Stat({ label, value, tone = "bg-gray-50 text-gray-700" }) {
  return (
    <div
      className={`rounded-2xl border border-gray-200 p-4 dark:border-white/10 ${tone}`}
    >
      <div className="text-sm">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-white/10">
      <div className="animate-pulse space-y-0">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-12 border-b border-gray-100 bg-gray-50/70 last:border-0 dark:border-white/10 dark:bg-white/[0.03]"
          />
        ))}
      </div>
    </div>
  );
}
